import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PDFViewer, Page, Text, View, Document, StyleSheet, Font, Svg } from '@react-pdf/renderer';
import axios from 'axios';
import './styles.css';
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Checkbox,
  MenuItem,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TextField,
  Typography,
  TableContainer,
  TablePagination,
} from '@mui/material';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';

import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

// ----------------------------------------------------------------------
// Kolone tabela
// search:    'exact' tačan broj | 'text' sadrži tekst | 'numeric' broj ili izraz (3, >0, <=2) | null bez pretrage
// searchKey: polje po kojem se pretražuje, ako je drugačije od polja po kojem se sortira
// sortable:  false - kolona se ne može sortirati

const TABLE_HEAD_ACCOUNTANTS = [
  { id: 'ime', label: 'Ime', alignRight: false, search: 'text', searchKey: 'imePrezime' },
  { id: 'prezime', label: 'Prezime', alignRight: false, search: 'text' },
  { id: 'telefon', label: 'Telefon', alignRight: false, search: 'text' },
  { id: 'mjesto', label: 'Mjesto', alignRight: false, search: 'text' },
  { id: 'drzava', label: 'Država', alignRight: false, search: 'text' },
  { id: 'select', label: 'Select', alignRight: false, search: null, sortable: false },
];

const TABLE_HEAD_BILLS = [
  { id: 'id', label: 'Broj', alignRight: false, search: 'exact' },
  { id: 'tip', label: 'Tip', alignRight: false, search: 'text' },
  { id: 'uredjaj', label: 'Uređaj', alignRight: false, search: 'text', searchKey: 'uredjajTrazi' },
  { id: 'datum', label: 'Datum', alignRight: false, search: 'text' },
  { id: 'datum_end_old', label: 'Datum stari', alignRight: false, search: 'text' },
  { id: 'datum_end_new', label: 'Datum novi', alignRight: false, search: 'text' },
  { id: 'pretplatnik', label: 'Pretplatnik', alignRight: false, search: 'text', searchKey: 'pretplatnikTrazi' },
  { id: 'accountantIme', label: 'Accountant', alignRight: false, search: 'text' },
  { id: 'iznos', label: 'Iznos', alignRight: false, search: 'numeric' },
  { id: 'booked', label: 'Booked', alignRight: false, search: null },
];

// ----------------------------------------------------------------------
// Sortiranje i pretraga (zajedničko za obje tabele)

function compareValues(a, b) {
  // Brojevi numerički, tekst abecedno (uz ispravno Č, Ć, Š, Ž, Đ)
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && a !== null && b !== null && !Number.isNaN(na) && !Number.isNaN(nb)) {
    return na - nb;
  }
  return String(a ?? '').localeCompare(String(b ?? ''), 'bs', { sensitivity: 'base' });
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => compareValues(b[orderBy], a[orderBy])
    : (a, b) => compareValues(a[orderBy], b[orderBy]);
}

// Brojčana pretraga: '3', '=3', '>0', '>=5', '<2', '<=1'
function matchNumeric(value, query) {
  const q = query.replace(/\s+/g, '').replace(',', '.');
  if (/^(>=|<=|>|<|=)?$/.test(q)) return true; // tek započet izraz, npr. samo '>'

  const m = q.match(/^(>=|<=|>|<|=)?(-?\d+(\.\d+)?)$/);
  if (!m) return false;

  const op = m[1] || '=';
  const n = Number(m[2]);
  const v = Number(value);

  switch (op) {
    case '>':
      return v > n;
    case '<':
      return v < n;
    case '>=':
      return v >= n;
    case '<=':
      return v <= n;
    default:
      return v === n;
  }
}

function matchesSearch(row, column, query) {
  if (!column) return true;
  const key = column.searchKey || column.id;
  const q = query.trim().toLowerCase();

  switch (column.search) {
    case 'exact':
      return /^\d+$/.test(q) ? Number(row[key]) === Number(q) : false;
    case 'text':
      return String(row[key] ?? '').toLowerCase().includes(q);
    case 'numeric':
      return matchNumeric(row[key], q);
    default:
      return true;
  }
}

function applySortFilter(array, comparator, query, column) {
  const sorted = array
    .map((el, index) => [el, index])
    .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
    .map((el) => el[0]);

  if (!query || !query.trim()) return sorted;

  return sorted.filter((row) => matchesSearch(row, column, query));
}

function getSearchPlaceholder(column) {
  if (!column) return 'Traži...';

  switch (column.search) {
    case 'exact':
      return `Traži po: ${column.label} (tačan broj)`;
    case 'text':
      return `Traži po: ${column.label}`;
    case 'numeric':
      return `Traži po: ${column.label} (npr. 20, >50, <=10)`;
    default:
      return `Pretraga nije dostupna za: ${column.label}`;
  }
}

// ----------------------------------------------------------------------
// Pretvaranje zapisa u ravne redove (da bi sortiranje i pretraga radili i za ugniježđena polja)

const punoIme = (osoba) => (osoba ? `${osoba.ime ?? ''} ${osoba.prezime ?? ''}`.trim() : '');

function accountantURed(a) {
  return { ...a, imePrezime: punoIme(a) };
}

function billURed(bill) {
  const device = bill.device || {};
  const sub = device.subscriber || {};
  return {
    ...bill,
    tip: device.tip ?? '',
    tiptrackera: device.tiptrackera ?? '',
    uredjaj: device.ime ?? '',
    uredjajTrazi: `${bill.deviceId ?? ''} ${device.ime ?? ''} ${device.IMEI ?? ''}`,
    pretplatnik: punoIme(sub),
    pretplatnikTrazi: `${punoIme(sub)} ${sub.mjesto ?? ''} ${sub.drzava ?? ''}`,
    accountantIme: punoIme(bill.accountant),
    iznos: Number(bill.iznos) || 0,
    booked: bill.booked ? 1 : 0,
  };
}

const formatIznos = (v) => `${(Number(v) || 0).toFixed(2)} KM`;

function saberi(rows) {
  return rows.reduce(
    (acc, b) => {
      acc.ukupno += b.iznos;
      if (b.booked) acc.proknjizeno += b.iznos;
      else acc.neproknjizeno += b.iznos;
      return acc;
    },
    { ukupno: 0, proknjizeno: 0, neproknjizeno: 0 }
  );
}

// ----------------------------------------------------------------------
// PDF font i stilovi

Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
});

const REPORT_COLS = [
  { key: 'rb', label: 'R.br.', width: '4%' },
  { key: 'id', label: 'Uplata', width: '6%' },
  { key: 'pretplatnik', label: 'Pretplatnik', width: '18%', left: true },
  { key: 'uredjaj', label: 'Pas', width: '12%', left: true },
  { key: 'IMEI', label: 'IMEI', width: '15%', left: true },
  { key: 'datum', label: 'Datum', width: '12%' },
  { key: 'datum_end_old', label: 'Datum stari', width: '9%' },
  { key: 'datum_end_new', label: 'Datum novi', width: '9%' },
  { key: 'iznos', label: 'Iznos', width: '9%' },
  { key: 'booked', label: 'Booked', width: '6%' },
];

const styles = StyleSheet.create({
  body: {
    paddingTop: 40,
    paddingBottom: 65,
    paddingHorizontal: 30,
    fontFamily: 'Roboto',
    fontSize: 10,
  },
  svgTitle: {
    marginVertical: 5,
    paddingHorizontal: 20,
  },
  textmediumlarge: {
    fontSize: 20,
    fontFamily: 'Roboto',
  },
  summary: {
    fontSize: 10,
    marginBottom: 12,
    paddingHorizontal: 20,
    lineHeight: 1.5,
  },
  table: {
    display: 'table',
    width: 'auto',
  },
  tableRow: {
    margin: 0,
    flexDirection: 'row',
  },
  tableHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    backgroundColor: '#e8ecf4',
  },
  rowOdd: {
    backgroundColor: '#f5f5f5',
  },
  tableCellLeft: {
    marginTop: 4,
    marginBottom: 4,
    marginLeft: 3,
    fontSize: 9,
    textAlign: 'left',
  },
  tableCellCenter: {
    marginTop: 4,
    marginBottom: 4,
    fontSize: 9,
    textAlign: 'center',
  },
  officer: {
    paddingTop: 50,
    left: 250,
  },
  officertext: {
    margin: 0,
    fontSize: 12,
    textAlign: 'center',
    fontFamily: 'Roboto',
  },
  mp: {
    position: 'absolute',
    fontSize: 12,
    bottom: 60,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
});

// PDF iznos sa "EUR" umjesto "€", za slučaj da font nema znak KM
const formatIznosPdf = (v) => `${(Number(v) || 0).toFixed(2)} KM`;

function BillsReport({ rows, datum, accountantIme }) {
  const zbir = saberi(rows);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.body} wrap>
        <Text style={styles.summary}>{datum}</Text>

        <Svg style={styles.svgTitle} width="800" height="40" viewBox="0 0 800 40">
          <Text style={styles.textmediumlarge} x="280" y="30">
            Evidencija naplate
          </Text>
        </Svg>

        <View style={styles.summary}>
          {accountantIme ? <Text>Accountant: {accountantIme}</Text> : null}
          <Text>Uplata u izvještaju: {rows.length}</Text>
          {/*
          <Text>
            Ukupno: {formatIznosPdf(zbir.ukupno)}   |   Proknjiženo: {formatIznosPdf(zbir.proknjizeno)}   |   Neproknjiženo:{' '}
            {formatIznosPdf(zbir.neproknjizeno)}
          </Text>
          */}
        </View>

        <View style={styles.table}>
          <View style={{ ...styles.tableRow, ...styles.tableHeader }}>
            {REPORT_COLS.map((c) => (
              <View key={c.key} style={{ width: c.width }}>
                <Text style={c.left ? styles.tableCellLeft : styles.tableCellCenter}>{c.label}</Text>
              </View>
            ))}
          </View>

          {rows.map((bill, index) => {
            const values = {
              rb: `${index + 1}.`,
              id: bill.id,
              pretplatnik: bill.pretplatnik,
              uredjaj: bill.uredjaj,
              IMEI: bill.device?.IMEI ?? '',
              datum: bill.datum,
              datum_end_old: bill.datum_end_old,
              datum_end_new: bill.datum_end_new,
              iznos: formatIznosPdf(bill.iznos),
              booked: bill.booked ? 'Da' : 'Ne',
            };

            return (
              <View
                key={bill.id}
                wrap={false}
                style={index % 2 ? { ...styles.tableRow, ...styles.rowOdd } : styles.tableRow}
              >
                {REPORT_COLS.map((c) => (
                  <View key={c.key} style={{ width: c.width }}>
                    <Text style={c.left ? styles.tableCellLeft : styles.tableCellCenter}>
                      {String(values[c.key] ?? '')}
                    </Text>
                  </View>
                ))}
              </View>
            );
          })}
        </View>

        <View style={styles.officer}>
          <Text style={styles.officertext}>______________________________</Text>
          <Text style={styles.officertext}>(Računovođa)</Text>
        </View>

        <Text style={styles.mp}>M.P.</Text>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
}

// ----------------------------------------------------------------------

// Broj iz role, npr. 'ROLE_ADMIN1' -> '1'
const brojIzRole = (role) => (role ? role.substring(10) : '');

export default function BillsReportAll() {
  const { user: currentUser } = useSelector((state) => state.auth);

  const [accountants, setAccountants] = useState([]);
  const [bills, setBills] = useState([]);
  const [accountant, setAccountant] = useState('0');
  const [accountantIme, setAccountantIme] = useState('');
  const [loading, setLoading] = useState(false);

  // Tabela accountanata
  const [pageAccountants, setPageAccountants] = useState(0);
  const [orderAccountants, setOrderAccountants] = useState('asc');
  const [orderByAccountants, setOrderByAccountants] = useState('ime');
  const [filterNameAccountants, setFilterNameAccountants] = useState('');
  const [rowsPerPageAccountants, setRowsPerPageAccountants] = useState(5);

  // Tabela uplata
  const [pageBills, setPageBills] = useState(0);
  const [orderBills, setOrderBills] = useState('desc');
  const [orderByBills, setOrderByBills] = useState('id');
  const [filterNameBills, setFilterNameBills] = useState('');
  const [rowsPerPageBills, setRowsPerPageBills] = useState(5);
  const [filterBooked, setFilterBooked] = useState('all');

  // --- Dohvatanje podataka ---

  const getAllAccountants = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/accountants/${brojIzRole(currentUser.roles[0])}`);
      setAccountants(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.log('Greška pri učitavanju accountanata', err);
    }
  };

  const getAllBills = async (accountantId) => {
    try {
      const response = await axios.get(`${SERVER_URL}/bills?accountantId=${accountantId}`);
      setBills(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.log('Greška pri učitavanju uplata', err);
    }
  };

  // Accountanti jednom, pri otvaranju stranice
  useEffect(() => {
    if (!currentUser) return;
    getAllAccountants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Uplate pri otvaranju i svaki put kad se izabere drugi accountant
  useEffect(() => {
    if (!currentUser) return;
    getAllBills(accountant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountant]);

  // --- Ravni redovi, sortiranje i filteri ---

  const accountantRows = useMemo(() => accountants.map(accountantURed), [accountants]);
  const billRows = useMemo(() => bills.map(billURed), [bills]);

  const accountantColumn = TABLE_HEAD_ACCOUNTANTS.find((c) => c.id === orderByAccountants);
  const billColumn = TABLE_HEAD_BILLS.find((c) => c.id === orderByBills);

  const filteredAccountants = useMemo(
    () =>
      applySortFilter(
        accountantRows,
        getComparator(orderAccountants, orderByAccountants),
        filterNameAccountants,
        accountantColumn
      ),
    [accountantRows, orderAccountants, orderByAccountants, filterNameAccountants, accountantColumn]
  );

  const filteredBills = useMemo(() => {
    const rows = applySortFilter(billRows, getComparator(orderBills, orderByBills), filterNameBills, billColumn);
    if (filterBooked === 'all') return rows;
    const trazeno = filterBooked === 'true' ? 1 : 0;
    return rows.filter((b) => b.booked === trazeno);
  }, [billRows, orderBills, orderByBills, filterNameBills, billColumn, filterBooked]);

  const zbir = useMemo(() => saberi(filteredBills), [filteredBills]);

  // Svi hookovi su iznad ove provjere
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // --- Handleri ---

  const handleChangeBooked = (id) => {
    const bill = bills.find((b) => b.id === id);
    if (!bill) return;
    const novoStanje = bill.booked ? 0 : 1;

    axios.put(`${SERVER_URL}/bills/${id}`, { booked: novoStanje }).then(() => {
      setBills((prev) => prev.map((b) => (b.id === id ? { ...b, booked: novoStanje } : b)));
    });
  };

  const handleSelectAccountant = (row) => {
    setAccountant(row.id);
    setAccountantIme(row.imePrezime);
    setPageBills(0);
  };

  const handleRequestSortAccountants = (event, property) => {
    const isAsc = orderByAccountants === property && orderAccountants === 'asc';
    setOrderAccountants(isAsc ? 'desc' : 'asc');
    if (property !== orderByAccountants) {
      setFilterNameAccountants('');
      setPageAccountants(0);
    }
    setOrderByAccountants(property);
  };

  const handleRequestSortBills = (event, property) => {
    const isAsc = orderByBills === property && orderBills === 'asc';
    setOrderBills(isAsc ? 'desc' : 'asc');
    if (property !== orderByBills) {
      setFilterNameBills('');
      setPageBills(0);
    }
    setOrderByBills(property);
  };

  const emptyRowsAccountants =
    pageAccountants > 0
      ? Math.max(0, (1 + pageAccountants) * rowsPerPageAccountants - filteredAccountants.length)
      : 0;

  const emptyRowsBills =
    pageBills > 0 ? Math.max(0, (1 + pageBills) * rowsPerPageBills - filteredBills.length) : 0;

  const today = new Date();
  const datum = `Datum: ${today.getDate()}.${today.getMonth() + 1}.${today.getFullYear()}. god.`;

  // --- Render ---

  return (
    <>
      <Helmet>
        <title> GPS Dogs Tracking System </title>
      </Helmet>

      <Container maxWidth={false}>
        {/* ---------------- Accountanti ---------------- */}

        <Typography variant="subtitle2" noWrap>
          Accountants
        </Typography>

        <Card>
          <UserListToolbar
            numSelected={0}
            filterName={filterNameAccountants}
            onFilterName={(e) => {
              setPageAccountants(0);
              setFilterNameAccountants(e.target.value);
            }}
            placeholder={getSearchPlaceholder(accountantColumn)}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={orderAccountants}
                  orderBy={orderByAccountants}
                  headLabel={TABLE_HEAD_ACCOUNTANTS}
                  onRequestSort={handleRequestSortAccountants}
                />
                <TableBody>
                  {filteredAccountants
                    .slice(
                      pageAccountants * rowsPerPageAccountants,
                      pageAccountants * rowsPerPageAccountants + rowsPerPageAccountants
                    )
                    .map((row) => {
                      const { id, ime, prezime, telefon, mjesto, drzava } = row;
                      const izabran = String(accountant) === String(id);

                      return (
                        <TableRow hover key={id} tabIndex={-1} selected={izabran}>
                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {ime}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {prezime}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {telefon}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {mjesto}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {drzava}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant={izabran ? 'contained' : 'text'}
                              onClick={() => handleSelectAccountant(row)}
                            >
                              {izabran ? 'Izabran' : 'Select'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                  {emptyRowsAccountants > 0 && (
                    <TableRow style={{ height: 53 * emptyRowsAccountants }}>
                      <TableCell colSpan={TABLE_HEAD_ACCOUNTANTS.length} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredAccountants.length}
            rowsPerPage={rowsPerPageAccountants}
            page={pageAccountants}
            onPageChange={(e, p) => setPageAccountants(p)}
            onRowsPerPageChange={(e) => {
              setPageAccountants(0);
              setRowsPerPageAccountants(parseInt(e.target.value, 10));
            }}
          />
        </Card>

        <br />

        {/* ---------------- Uplate ---------------- */}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, mt: 2 }}>
          <Typography variant="subtitle2" noWrap>
            Uplate{accountantIme ? ` - ${accountantIme}` : ''}
          </Typography>

          <TextField
            select
            size="small"
            label="Booked"
            value={filterBooked}
            onChange={(e) => {
              setFilterBooked(e.target.value);
              setPageBills(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">Sve</MenuItem>
            <MenuItem value="true">Proknjižene</MenuItem>
            <MenuItem value="false">Neproknjižene</MenuItem>
          </TextField>
        </Stack>

        <Card>
          <UserListToolbar
            numSelected={0}
            filterName={filterNameBills}
            onFilterName={(e) => {
              setPageBills(0);
              setFilterNameBills(e.target.value);
            }}
            placeholder={getSearchPlaceholder(billColumn)}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={orderBills}
                  orderBy={orderByBills}
                  headLabel={TABLE_HEAD_BILLS}
                  onRequestSort={handleRequestSortBills}
                />
                <TableBody>
                  {filteredBills
                    .slice(pageBills * rowsPerPageBills, pageBills * rowsPerPageBills + rowsPerPageBills)
                    .map((row) => {
                      const {
                        id,
                        deviceId,
                        device,
                        tip,
                        tiptrackera,
                        datum: datumUplate,
                        datum_end_old,
                        datum_end_new,
                        pretplatnik,
                        accountantIme: imeAccountanta,
                        iznos,
                        booked,
                      } = row;
                      const sub = device?.subscriber || {};

                      return (
                        <TableRow hover key={id} tabIndex={-1}>
                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {id}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar
                                alt={tip}
                                src={tip ? `/assets/images/lovackipsi/icon_${tip}.png` : undefined}
                                sx={{ width: 50, height: 50 }}
                                variant="rounded"
                              >
                                <Iconify icon="mdi:paw" width={28} />
                              </Avatar>
                              <Avatar
                                alt={tiptrackera}
                                src={
                                  tiptrackera
                                    ? `/assets/images/trackers/icon_${tiptrackera.toLowerCase()}.png`
                                    : undefined
                                }
                                sx={{ width: 30, height: 30 }}
                                variant="rounded"
                              >
                                <Iconify icon="mdi:crosshairs-gps" width={18} />
                              </Avatar>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {device?.ime}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                              ID {deviceId}, IMEI {device?.IMEI}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                              Saldo: {device?.saldo}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {datumUplate}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {datum_end_old}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {datum_end_new}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {pretplatnik}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                              {sub.mjesto}, {sub.drzava}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {imeAccountanta}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="subtitle2" noWrap>
                              {formatIznos(iznos)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Checkbox
                              color="success"
                              checked={Boolean(booked)}
                              onChange={() => handleChangeBooked(id)}
                              inputProps={{ 'aria-label': 'booked' }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}

                  {emptyRowsBills > 0 && (
                    <TableRow style={{ height: 53 * emptyRowsBills }}>
                      <TableCell colSpan={TABLE_HEAD_BILLS.length} />
                    </TableRow>
                  )}
                </TableBody>

                {!filteredBills.length && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={TABLE_HEAD_BILLS.length} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="body2">
                            {filterNameBills
                              ? `Ništa nije pronađeno za "${filterNameBills}" u koloni ${billColumn?.label}.`
                              : 'Nema uplata za izabrani filter.'}
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            sx={{ px: 3, pt: 2 }}
          >
            {/*
            <Typography variant="body2">
              Ukupno: <strong>{formatIznos(zbir.ukupno)}</strong>
              {'   '}|{'   '}Proknjiženo: <strong>{formatIznos(zbir.proknjizeno)}</strong>
              {'   '}|{'   '}Neproknjiženo: <strong>{formatIznos(zbir.neproknjizeno)}</strong>
            </Typography>
            */}
            <div />

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredBills.length}
              rowsPerPage={rowsPerPageBills}
              page={pageBills}
              onPageChange={(e, p) => setPageBills(p)}
              onRowsPerPageChange={(e) => {
                setPageBills(0);
                setRowsPerPageBills(parseInt(e.target.value, 10));
              }}
            />
          </Stack>
        </Card>

        <br />

        <Button onClick={() => setLoading(!loading)}>
          {loading ? 'Zatvori izvještaj' : 'Generate Bills Report'}
        </Button>

        {loading && (
          <div className="child">
            <PDFViewer className="child">
              <BillsReport rows={filteredBills} datum={datum} accountantIme={accountantIme} />
            </PDFViewer>
          </div>
        )}
      </Container>
    </>
  );
}